import * as React from "react";
import { useNavigate } from "react-router";
import { RdPopover } from "../../redesign/table/RdPopover";
import { KontaktmoeglichkeitCell } from "../../redesign/KontaktmoeglichkeitPopup";
import type { ContactDetailsFormValues } from "../../../types/Contact";
import { useAnsprechpartner } from "../detail/useAnsprechpartner";
import "./vorgangV3.css";

/**
 * Ansprechpartner der Firma als KNOPF MIT ZAHL in der Kontaktzeile
 * (PO 09.09.2026: „Ansprechpartner ein Button mit Zahl, Popup Liste der AP mit
 * Kommunikation").
 *
 * Vorgänger war die {@link ./AnsprechpartnerZeile} — eine eigene Zeile, die
 * sich nach unten aufklappte. Sie kostete eine ganze Kopfzeile für eine Angabe,
 * die man selten braucht und dann sofort: „wen rufe ich bei der Firma an?" Als
 * Knopf steht sie in derselben Zeile wie der Kunde, zu dem sie gehört.
 *
 * Zwei Entscheidungen:
 *  · Nur bei einer FIRMA — bei einer Privatperson wären dieselben Beziehungen
 *    Angehörige, keine Ansprechpartner.
 *  · Geladen wird SOFORT, nicht erst beim Öffnen. Die Vorgängerin lud erst beim
 *    Aufklappen und sparte damit einen Abruf; eine Zahl, die erst nach dem
 *    Klick erscheint, ist aber keine Zahl. Solange nichts geladen ist, trägt
 *    der Knopf gar keine — kein „0", das nach „keine hinterlegt" aussähe.
 *
 * Die Kommunikation zeigt `KontaktmoeglichkeitCell`: dieselben zwei Icons wie
 * in der Kontakttabelle, dahinter das bekannte Modal mit allen Nummern und
 * Adressen der Person.
 */
export const AnsprechpartnerKnopf: React.FC<{
    kontaktId: number | null;
    /** Nur eine Firma hat Ansprechpartner (`kunde.type.type === "Firma"`). */
    istFirma: boolean;
}> = ({ kontaktId, istFirma }) => {
    const navigate = useNavigate();
    // Der Hook lädt nur, wenn `aktiv` — hier also genau dann, wenn die Zeile
    // den Knopf überhaupt zeigt.
    const { personen, geladen } = useAnsprechpartner(kontaktId, kontaktId != null && istFirma);

    if (kontaktId == null || !istFirma) return null;

    const name = geladen
        ? `Ansprechpartner (${personen.length})`
        : "Ansprechpartner";

    return (
        <RdPopover
            width={320}
            align="end"
            dialogLabel="Ansprechpartner der Firma"
            trigger={({ toggle, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    className="vg3-ap-knopf"
                    title={name}
                    aria-label={name}
                    onClick={toggle}
                >
                    <span>Ansprechpartner</span>
                    {geladen && <span className="vg3-ap-zahl">{personen.length}</span>}
                </button>
            )}
        >
            {() => (
                // Portal an `body` — außerhalb des `.kvp-rd`-Wrappers der Seite;
                // ohne eigene Hülle griffen weder Regeln noch Farbvariablen.
                <div className="kvp-rd vg3-ap-liste">
                    {!geladen ? (
                        <div className="vg3-ap-hinweis">Laden…</div>
                    ) : personen.length === 0 ? (
                        <div className="vg3-ap-hinweis">Keine Ansprechpartner hinterlegt.</div>
                    ) : (
                        personen.map((p) => (
                            <div key={p.id} className="vg3-ap-row">
                                <button
                                    type="button"
                                    className="vg3-ap-name"
                                    title="Kontakt öffnen"
                                    onClick={() => navigate(`/contacts/${p.id}`)}
                                >
                                    {p.name}
                                </button>
                                <div className="vg3-ap-funcs">
                                    {p.funcs.map((f) => (
                                        <span key={f} className="vg3-ap-func">
                                            {f}
                                        </span>
                                    ))}
                                </div>
                                {/* Das Popup lädt sein Detail selbst nach — es
                                    braucht nur Id, Typ und Anzeigename. */}
                                <KontaktmoeglichkeitCell
                                    row={
                                        {
                                            kontaktId: p.id,
                                            typ: "Person",
                                            displayName: p.name,
                                        } as unknown as ContactDetailsFormValues
                                    }
                                />
                            </div>
                        ))
                    )}
                </div>
            )}
        </RdPopover>
    );
};
